import { ICommandBarItemProps } from '@fluentui/react';
import { useThemeData } from '@msrceng/theme';
import { useTranslation } from 'react-i18next';
import { useMakeMainButtonText } from './useMakeMainButtonText';
import { useDateSelectorEditDate } from './logic/useDateSelectorEditDate';
import { useDateSelectorItems } from './useDateSelectorItems';
import { Dispatch, SetStateAction } from 'react';

/**
 * The button that opens up the date selector.
 */
export const useDateSelector = ({
  startDateExternal,
  endDateExternal,
  setStartDateExternal,
  setEndDateExternal,
}: {
  startDateExternal: Date | undefined;
  endDateExternal: Date | undefined;
  setStartDateExternal: Dispatch<SetStateAction<Date | undefined>>;
  setEndDateExternal: Dispatch<SetStateAction<Date | undefined>>;
}): ICommandBarItemProps => {
  const [t] = useTranslation(['search', 'translation']);
  const { theme } = useThemeData();

  const { setEndDate, setStartDate, endDate, startDate } =
    useDateSelectorEditDate();

  const text: ICommandBarItemProps['text'] = useMakeMainButtonText();
  const title: ICommandBarItemProps['title'] = t('Select date range', {
    defaultValue: 'Select date range',
    ns: 'search',
  });

  /** Save user's current changes to source data */
  const onSave = () => {
    setStartDateExternal(startDate);
    setEndDateExternal(endDate);
  };

  const items = useDateSelectorItems({
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    onSave,
  });

  return {
    key: 'date',
    text,
    title: t('changeDates', { ns: 'search', defaultValue: 'Change Dates' }),
    iconProps: { iconName: 'Calendar' },
    subMenuProps: {
      //See https://msazure.visualstudio.com/One/_workitems/edit/17229089 for why i'm doing the below
      calloutProps: {
        role: 'dialog',
        setInitialFocus: true,
      },
      onRestoreFocus: params => {
        params.originalElement?.focus();
      },
      styles: {
        container: {
          border: `1px outset ${theme?.palette?.neutralLight}`,
        },
        title: {
          background: 'transparent',
          color: theme?.palette?.themePrimary,
        },
        root: {
          padding: '0.5em 1em',
        },
      },
      title,
      items,
    },
  };
};
